const chapter = {
  id: 'sexual-reproduction-flowering-plants',
  slug: 'sexual-reproduction-flowering-plants',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Sexual Reproduction in Flowering Plants',
        hindi: 'पुष्पीय पादपों में लैंगिक जनन',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Reproduction', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'flower-structure',
      title: {
        english: 'Flower — A Modified Shoot',
        hindi: 'पुष्प — एक रूपान्तरित प्ररोह',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Flower = reproductive unit of angiosperms; modified shoot for sexual reproduction.',
        hindi: 'फूल = आवृतबीजी पौधों की प्रजनन इकाई; लैंगिक प्रजनन के लिए संशोधित प्ररोह।',
            },
            {
              english: 'Four whorls — calyx (sepals), corolla (petals), **androecium** (stamens — male), **gynoecium** (carpels — female).',
        hindi: 'चार चक्र - कैलेक्स (बाह्यदल), कोरोला (पंखुड़ियाँ), **एंड्रोइकियम** (पुंकेसर - नर), **गाइनोइकियम** (कार्पेल - मादा)।',
            },
            {
              english: 'Stamen = filament + anther. **Anther** is typically **dithecous** (2 lobes) and **tetrasporangiate** (4 microsporangia / pollen sacs).',
        hindi: 'Stamen = filament + anther। **एंथेर** आम तौर पर **डाइथेकस** (2 लोब) और **टेट्रास्पोरैंगिएट** (4 माइक्रोस्पोरंगिया / परागकोष) होता है।',
            },
            {
              english: 'Pistil/Carpel = stigma + style + ovary. Ovary contains ovules attached on placenta.',
        hindi: 'Pistil/. अंडाशय में प्लेसेंटा से जुड़े अंडाणु होते हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'microsporogenesis',
      title: {
        english: 'Microsporogenesis & Pollen',
        hindi: 'लघुबीजाणुजनन और परागकण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Anther wall (outer → inner): **epidermis → endothecium → middle layers → tapetum**.',
        hindi: 'एथर दीवार (बाहरी → भीतरी): **एपिडर्मिस → एंडोथेसियम → मध्य परतें → टेपेटम**। **टेपेटम** = विकासशील पराग को पोषण देता है; कोशिकाओं में अक्सर **सघन साइटोप्लाज्म + एकाधिक नाभिक** (मल्टीन्यूक्लिएट/पॉलीप्लोइड) होते हैं।',
            },
            {
              english: '**Tapetum** = nourishes developing pollen; cells often have **dense cytoplasm + multiple nuclei** (multinucleate / polyploid).',
        hindi: '**टेपेटम** = विकासशील पराग को पोषण देता है; कोशिकाओं में अक्सर **सघन साइटोप्लाज्म + एकाधिक नाभिक** (मल्टीन्यूक्लिएट/पॉलीप्लोइड) होते हैं।',
            },
            {
              english: '**Microsporogenesis** — microspore mother cells (MMC, 2n) undergo **meiosis** → **microspore tetrad** (4 haploid microspores; usually tetrahedral arrangement).',
        hindi: '**माइक्रोस्पोरोजेनेसिस** - माइक्रोस्पोर मातृ कोशिकाएं (एमएमसी, 2एन) **अर्धसूत्रीविभाजन** → **माइक्रोस्पोर टेट्राड** (4 अगुणित माइक्रोस्पोर; आमतौर पर टेट्राहेड्रल व्यवस्था) से गुजरती हैं।',
            },
            {
              english: 'Microspores dissociate → **pollen grains**.',
        hindi: 'माइक्रोस्पोर्स अलग हो जाते हैं → **पराग कण**।',
            },
            {
              english: 'Pollen wall = outer **exine** (sporopollenin — most resistant organic material; preserved in fossils) + inner **intine** (cellulose + pectin).',
        hindi: 'पराग दीवार = बाहरी **एक्सिन** (स्पोरोपोलेनिन - सबसे प्रतिरोधी कार्बनिक पदार्थ; जीवाश्मों में संरक्षित) + आंतरिक **इन्टिन** (सेलूलोज़ + पेक्टिन)।',
            },
            {
              english: 'Exine has **germ pores** where sporopollenin is absent.',
        hindi: 'एक्साइन में **रोगाणु छिद्र** होते हैं जहां स्पोरोपोलेनिन अनुपस्थित होता है।',
            },
            {
              english: 'Mature pollen at shedding: **2-celled** (vegetative + generative) in 60 % of angiosperms; **3-celled** (generative divides into 2 male gametes inside pollen tube) in remaining 40 %.',
        hindi: 'झड़ने के समय परिपक्व पराग: 60% आवृतबीजी में **2-कोशिका** (वानस्पतिक + जनन); शेष 40% में **3-कोशिका** (जनन पराग नलिका के अंदर 2 नर युग्मकों में विभाजित होता है)।',
            },
            {
              english: 'Pollen viability — Rice/Wheat: ~30 min; Rosaceae/Leguminosae/Solanaceae: months. Stored at −196 °C in liquid N₂ (pollen banks).',
        hindi: 'पराग व्यवहार्यता - Rice/Wheat: ~30 मिनट; Rosaceae/Leguminosae/सोलानेसी: महीने। तरल N₂ (पराग बैंक) में -196 डिग्री सेल्सियस पर संग्रहित।',
            },
            {
              english: 'Pollen allergy — *Parthenium* (carrot grass) causes allergic bronchitis & asthma.',
        hindi: 'पराग एलर्जी - *पार्थेनियम* (गाजर घास) एलर्जी ब्रोंकाइटिस और अस्थमा का कारण बनता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'megasporogenesis',
      title: {
        english: 'Megasporogenesis & Embryo Sac',
        hindi: 'गुरुबीजाणुजनन और भ्रूण कोश',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Ovule (megasporangium) — funicle, hilum, integuments (1-2), micropyle, chalaza, nucellus.',
        hindi: 'बीजांड (मेगास्पोरैंगियम) - कवक, हिलम, पूर्णांक (1-2), माइक्रोपाइल, चालाज़ा, न्युसेलस।',
            },
            {
              english: '**MMC** in nucellus undergoes **meiosis** → **4 megaspores** (linear tetrad). Usually only **chalazal megaspore** is functional; rest degenerate.',
        hindi: '**एमएमसी** न्युकेलस में **अर्धसूत्रीविभाजन** → **4 मेगास्पोर** (रैखिक टेट्राड) से गुजरता है। आमतौर पर केवल **चैलाज़ल मेगास्पोर** ही क्रियाशील होता है; बाकी पतित.',
            },
            {
              english: 'Functional megaspore → **female gametophyte / embryo sac** (monosporic development = **Polygonum type**, most common).',
        hindi: 'कार्यात्मक मेगास्पोर → **मादा गैमेटोफाइट / भ्रूण थैली** (मोनोस्पोरिक development = **Polygonum type**, सबसे आम)।',
            },
            {
              english: 'Mitosis: 1 nucleus → 2 → 4 → **8 nuclei** (free nuclear stage) → **7-celled, 8-nucleate** structure:',
        hindi: 'समसूत्री विभाजन: 1 केन्द्रक → 2 → 4 → **8 केन्द्रक** (मुक्त परमाणु चरण) → **7-कोशिका, 8-केन्द्रक** संरचना:',
            },
            {
              english: '• 3 cells at micropylar end = **egg apparatus** (1 egg + 2 synergids; synergids have **filiform apparatus** to guide pollen tube).',
        hindi: '• माइक्रोपाइलर पर 3 कोशिकाएं end = **egg apparatus** (1 egg + 2 synergids; परागनलिका को निर्देशित करने के लिए सिनर्जिड में **फ़िलिफ़ॉर्म उपकरण** होता है)।',
            },
            {
              english: '• 3 cells at chalazal end = **antipodals**.',
        hindi: '• चालाज़ल पर 3 कोशिकाएँ end = **antipodals**।',
            },
            {
              english: '• 1 large central cell with **2 polar nuclei**.',
        hindi: '**2 ध्रुवीय नाभिक** के साथ 1 बड़ी केंद्रीय कोशिका।',
            },
          ],
        },
      ],
    },
    {
      id: 'pollination',
      title: {
        english: 'Pollination',
        hindi: 'परागण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Transfer of pollen from anther to stigma. Three kinds:',
        hindi: 'परागकोश से वर्तिकाग्र तक पराग का स्थानांतरण। तीन प्रकार:',
            },
            {
              english: '• **Autogamy** — within same flower (self-pollination). Requires synchrony + position. **Cleistogamous flowers** (closed; e.g. *Viola, Oxalis, Commelina*) ensure obligate autogamy.',
        hindi: '• **ऑटोगैमी** - एक ही फूल के भीतर (स्व-परागण)। समकालिकता + स्थिति की आवश्यकता है. **क्लिस्टोगैमस फूल** (बंद; जैसे *वायोला, ऑक्सालिस, कोमेलिना*) अनिवार्य ऑटोगैमी सुनिश्चित करते हैं।',
            },
            {
              english: '• **Geitonogamy** — between two flowers of same plant (genetically autogamy but functionally cross-pollination by pollinator).',
        hindi: '• **गीटोनोगैमी** - एक ही पौधे के दो फूलों के बीच (आनुवंशिक रूप से ऑटोगैमी लेकिन परागणकर्ता द्वारा कार्यात्मक रूप से क्रॉस-परागण)।',
            },
            {
              english: '• **Xenogamy** — between flowers of different plants (true cross-pollination → genetic variation).',
        hindi: '• **ज़ेनोगैमी** - विभिन्न पौधों के फूलों के बीच (सच्चा क्रॉस-परागण → आनुवंशिक भिन्नता)। **एजेंट** —',
            },
            {
              english: '**Agents** —',
        hindi: '**एजेंट** —',
            },
            {
              english: '• Abiotic: **wind (anemophily)** — light, dry pollen; large feathery stigma; e.g. grasses, maize. **Water (hydrophily)** — rare; *Vallisneria* (epihydrophily, surface), *Zostera* (sea grass — pollen long ribbon-like, hypohydrophily).',
        hindi: '• अजैविक: **हवा (एनेमोफिली)** - हल्का, सूखा पराग; बड़े पंखदार कलंक; जैसे घास, मक्का. **जल (हाइड्रोफिली)** - दुर्लभ; *वालिसनेरिया* (एपिहाइड्रोफिली, सतह), *ज़ोस्टेरा* (समुद्री घास - पराग लंबे रिबन की तरह, हाइपोहाइड्रोफिली)।',
            },
            {
              english: '• Biotic: **insects (entomophily)** — most common; flowers large, coloured, fragrant, with nectar. **Birds (ornithophily)** — Bombax. **Bats (chiropterophily)** — *Adansonia* (baobab).',
        hindi: '• जैविक: **कीड़े (एंटोमोफिली)** — सबसे आम; फूल बड़े, रंगीन, सुगंधित, रसयुक्त। **पक्षी (ऑर्निथोफिली)** - बॉमबैक्स। **चमगादड़ (चिरोप्टेरोफिली)** - *अडानसोनिया* (बाओबाब)।',
            },
            {
              english: 'Pollen-pistil interaction — pistil **recognises** compatible vs incompatible pollen via chemical dialogue; promotes germination of compatible pollen → **pollen tube** grows through style.',
        hindi: 'पराग-स्त्रीकेसर अंतःक्रिया - स्त्रीकेसर रासायनिक संवाद के माध्यम से संगत बनाम असंगत पराग को **पहचानता** है; संगत पराग के अंकुरण को बढ़ावा देता है → **पराग नलिका** शैली के माध्यम से बढ़ती है।',
            },
            {
              english: 'Outbreeding devices — pollen release & stigma receptivity not synchronised; anther & stigma at different positions; **self-incompatibility** (genetic, prevents self-pollen germination); production of unisexual flowers (papaya — dioecious; maize — monoecious).',
        hindi: 'आउटब्रीडिंग डिवाइस - पराग रिलीज और कलंक ग्रहणशीलता सिंक्रनाइज़ नहीं है; विभिन्न स्थानों पर परागकोश और वर्तिकाग्र; **स्व-असंगतता** (आनुवंशिक, स्व-पराग अंकुरण को रोकता है); एकलिंगी फूलों का उत्पादन (पपीता - द्विलिंगी; मक्का - एकलिंगी)।',
            },
          ],
        },
      ],
    },
    {
      id: 'double-fertilisation',
      title: {
        english: 'Double Fertilisation',
        hindi: 'द्विनिषेचन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Pollen tube enters ovule through micropyle (porogamy, common); rarely through chalaza (chalazogamy, *Casuarina*) or integument (mesogamy, *Cucurbita*). Tube enters one synergid (filiform apparatus guides), discharges 2 male gametes.',
        hindi: 'पराग नली माइक्रोपाइल (पोरोगेमी, सामान्य) के माध्यम से बीजांड में प्रवेश करती है; शायद ही कभी चालाज़ा (चैलाज़ोगैमी, *कैसुरीना*) या पूर्णांक (मेसोगैमी, *कुकुर्बिटा*) के माध्यम से। ट्यूब एक सिनर्जिड (फ़िलिफ़ॉर्म उपकरण गाइड) में प्रवेश करती है, 2 नर युग्मकों को मुक्त करती है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Syngamy** — male gamete (n) + egg (n) → **zygote (2n)**.',
        hindi: '**सिनगैमी** — नर युग्मक (एन) + अंडाणु (एन) → **जाइगोट (2एन)**। **ट्रिपल फ्यूजन** - दूसरा नर युग्मक (एन) + 2 ध्रुवीय नाभिक (एन + एन) → **पीईएन (प्राथमिक एंडोस्पर्म न्यूक्लियस, 3एन) → एंडोस्पर्म**।',
            },
            {
              english: '**Triple fusion** — second male gamete (n) + 2 polar nuclei (n + n) → **PEN (Primary Endosperm Nucleus, 3n) → Endosperm**.',
        hindi: '**त्रिगुण संलयन** - दूसरा नर युग्मक (एन) + 2 ध्रुवीय नाभिक (एन + एन) → **पेन (प्राथमिक एंडोस्पर्म न्यूक्लियस, 3एन) → एंडोस्पर्म**।',
            },
            {
              english: 'Both events in same embryo sac → **DOUBLE FERTILISATION** (unique to angiosperms). Discovered by **S.G. Nawaschin (1898) in *Fritillaria* & *Lilium***.',
        hindi: 'दोनों घटनाएं एक ही भ्रूण थैली में → **दोहरा निषेचन** (एंजियोस्पर्म के लिए अद्वितीय)। **एस.जी. द्वारा खोजा गया। नवास्चिन (1898) *फ्रिटिलारिया* और *लिलियम*** में।',
            },
          ],
        },
      ],
    },
    {
      id: 'post-fertilisation',
      title: {
        english: 'Post-fertilisation Events',
        hindi: 'निषेचन-पश्चात् घटनाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Endosperm develops first (provides nutrition to developing embryo). Three types:',
        hindi: 'भ्रूणपोष सबसे पहले विकसित होता है (विकासशील भ्रूण को पोषण प्रदान करता है)। तीन प्रकार:',
            },
            {
              english: '• **Nuclear** (free nuclear divisions → wall later; coconut water = liquid nuclear endosperm; coconut meat = cellular endosperm).',
        hindi: '• **परमाणु** (मुक्त परमाणु विभाजन → दीवार बाद में; नारियल water = liquid nuclear endosperm; नारियल meat = cellular endosperm)।',
            },
            {
              english: '• **Cellular** (cell-wall formation from beginning).',
        hindi: '• **सेल्युलर** (शुरुआत से कोशिका-दीवार का निर्माण)।',
            },
            {
              english: '• **Helobial** (intermediate).',
        hindi: '• **हेलोबियल** (मध्यवर्ती)।',
            },
            {
              english: 'Embryo development — **Dicot**: globular → heart-shaped → torpedo → mature embryo (radicle, plumule, **2 cotyledons**). **Monocot**: 1 large cotyledon (**scutellum**) + plumule (covered by **coleoptile**) + radicle (covered by **coleorhiza**).',
        hindi: 'भ्रूण विकास - **डाइकोट**: गोलाकार → दिल के आकार का → टारपीडो → परिपक्व भ्रूण (रेडिकल, प्लम्यूल, **2 बीजपत्र**)। **मोनोकोट**: 1 बड़ा बीजपत्र (**स्कूटेलम**) + प्लम्यूल (**कोलियोप्टाइल** से ढका हुआ) + मूलांकुर (**कोलेओरिज़ा** से ढका हुआ)।',
            },
            {
              english: 'Ovule → seed. Integuments → seed coat (testa + tegmen). Ovary → fruit. Ovary wall → pericarp.',
        hindi: 'बीजांड → बीज। पूर्णांक → बीज आवरण (टेस्टा + टेगमेन)। अंडाशय → फल. अंडाशय की दीवार → पेरिकारप।',
            },
            {
              english: 'Albuminous seeds (endosperm persists; e.g. wheat, maize, castor, coconut). Non-albuminous (endosperm consumed; pea, groundnut, beans).',
        hindi: 'एल्बुमिनस बीज (एंडोस्पर्म बना रहता है; जैसे गेहूं, मक्का, अरंडी, नारियल)। गैर-एल्ब्यूमिनस (एण्डोस्पर्म का सेवन; मटर, मूंगफली, सेम)।',
            },
            {
              english: 'Perisperm — residual nucellus (e.g. black pepper, beet).',
        hindi: 'पेरिस्पर्म - अवशिष्ट न्युकेलस (जैसे काली मिर्च, चुकंदर)। **एपोमिक्सिस** = बिना निषेचन के बीज का उत्पादन (अलैंगिक, यौन की नकल)। एस्टेरसिया और घासों में आम। संकर बीज उद्योग में उपयोगी - साल दर साल संकर शक्ति को बरकरार रखता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'apomixis-polyembryony',
      title: {
        english: 'Apomixis, Polyembryony, Parthenocarpy',
        hindi: 'असंगजनन, बहुभ्रूणता, अनिषेकजनन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Apomixis** = production of seed without fertilisation (asexual mimicking sexual). Common in Asteraceae & grasses. Useful in hybrid seed industry — preserves hybrid vigour year after year.',
        hindi: '**एपोमिक्सिस** = निषेचन के बिना बीज का उत्पादन (अलैंगिक, यौन की नकल)। एस्टेरसिया और घासों में आम। संकर बीज उद्योग में उपयोगी - साल दर साल संकर शक्ति को बरकरार रखता है।',
            },
            {
              english: '**Polyembryony** = more than one embryo in a seed. Citrus, mango (nucellar polyembryony — adventive embryos from nucellus cells, diploid).',
        hindi: '**बहुभ्रूण** = एक बीज में एक से अधिक भ्रूण। साइट्रस, आम (न्युसेलर पॉलीएम्ब्रायनी - न्युसेलस कोशिकाओं से आगमनात्मक भ्रूण, द्विगुणित)। **पार्थेनोकार्पी** = बिना निषेचन के फल का विकास → बीज रहित (केला, अंगूर)। ऑक्सिन/जीए द्वारा प्रेरित किया जा सकता है।',
            },
            {
              english: '**Parthenocarpy** = development of fruit without fertilisation → seedless (banana, grapes). Can be induced by auxins / GA.',
        hindi: '**पार्थेनोकार्पी** = बिना निषेचन के फल का विकास → बीज रहित (केला, अंगूर)। ऑक्सिन/जीए द्वारा प्रेरित किया जा सकता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Anther: dithecous, tetrasporangiate (4 pollen sacs)}',
      desc: {
        english: '',
        hindi: 'परागकोष: डाइथेकस, टेट्रास्पोरैंगियेट (4 परागकोष)',
      },
    },
    {
      latex: '\\text{MMC (2n)} \\xrightarrow{\\text{meiosis}} \\text{microspore tetrad (4 \\times n)}',
      desc: {
        english: '',
        hindi: 'एमएमसी (2एन) माइक्रोस्पोर टेट्राड (4 एन)',
      },
    },
    {
      latex: '\\text{Embryo sac: 7-celled, 8-nucleate (Polygonum / monosporic)}',
      desc: {
        english: '',
        hindi: 'भ्रूणकोष: 7-कोशिका, 8-न्यूक्लिएट (बहुभुज/मोनोस्पोरिक)',
      },
    },
    {
      latex: '\\text{Syngamy: } n + n \\rightarrow 2n \\text{ (zygote)}',
      desc: {
        english: '',
        hindi: 'पर्यायवाची: n + n 2n (युग्मनज)',
      },
    },
    {
      latex: '\\text{Triple fusion: } n + (n + n) \\rightarrow 3n \\text{ (PEN/endosperm)}',
      desc: {
        english: '',
        hindi: 'त्रिगुण संलयन: n + (n + n) 3n (PEN/एंडोस्पर्म)',
      },
    },
    {
      latex: '\\text{Double fertilisation = unique to angiosperms (Nawaschin, 1898)}',
      desc: {
        english: '',
        hindi: 'दोहरा निषेचन = आवृतबीजी पौधों के लिए अद्वितीय (नवासचिन, 1898)',
      },
    },
    {
      latex: '\\text{Ovule} \\rightarrow \\text{Seed; Ovary} \\rightarrow \\text{Fruit; Integuments} \\rightarrow \\text{Seed coat}',
      desc: {
        english: '',
        hindi: 'अंडाकार बीज; अंडाशय फल; पूर्णांक बीज आवरण',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Embryo sac',
      problem: {
        english: 'Polygonum type embryo sac is:',
        hindi: 'पॉलीगोनम प्रकार की भ्रूण थैली है:',
      },
      answer: {
        english: '7-celled, 8-nucleate, monosporic',
        hindi: '7-कोशिका, 8-न्यूक्लिएट, मोनोस्पोरिक',
      },
      solution: {
        english: 'Egg apparatus (3) + antipodals (3) + 1 central cell with 2 polar nuclei.',
        hindi: 'अंडा उपकरण (3) + एंटीपोडल्स (3) + 2 ध्रुवीय नाभिक के साथ 1 केंद्रीय कोशिका।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Endosperm',
      problem: {
        english: 'Coconut water is:',
        hindi: 'नारियल पानी है:',
      },
      answer: {
        english: 'Free nuclear endosperm',
        hindi: 'मुक्त परमाणु भ्रूणपोष',
      },
      solution: {
        english: 'White meat = cellular endosperm formed later.',
        hindi: 'सफ़ेद मांस = बाद में बना कोशिकीय भ्रूणपोष।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Pollen',
      problem: {
        english: 'Most resistant material in pollen wall is:',
        hindi: 'पराग दीवार में सबसे प्रतिरोधी सामग्री है:',
      },
      answer: {
        english: 'Sporopollenin',
        hindi: 'स्पोरोपोलेनिन',
      },
      solution: {
        english: 'Found in exine; preserved in fossils.',
        hindi: 'exine में मिला; जीवाश्मों में संरक्षित.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Fertilisation',
      problem: {
        english: 'Double fertilisation was discovered by:',
        hindi: 'दोहरे निषेचन की खोज किसके द्वारा की गई थी:',
      },
      answer: {
        english: 'S.G. Nawaschin (1898)',
        hindi: 'एस.जी. नवास्चिन (1898)',
      },
      solution: {
        english: 'In Fritillaria and Lilium.',
        hindi: 'फ्रिटिलारिया और लिलियम में।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Outbreeding',
      problem: {
        english: 'Cleistogamous flowers ensure:',
        hindi: 'क्लिस्टोगैमस फूल सुनिश्चित करते हैं:',
      },
      answer: {
        english: 'Obligate autogamy',
        hindi: 'ऑटोगैमी को बाध्य करें',
      },
      solution: {
        english: 'Flowers do not open; self-pollination is guaranteed.',
        hindi: 'फूल नहीं खिलते; स्व-परागण की गारंटी है।',
      },
    },
  ],
  next: {
    slug: 'human-reproduction',
    title: 'Human Reproduction',
  },
};

export default chapter;
